/**
 * Inline non-fatal error / warning strip rendered above the chat.
 *
 * Used for soft failures where the chat is still usable but the
 * user needs to know something is wrong — currently driven by a
 * rejected rate limit. Fatal errors take the full-pane
 * :class:`SessionCrash` instead.
 *
 * Shape mirrors the in-turn :class:`TurnNoticePill`: icon +
 * sentence-case headline + optional one-line hint, banded by
 * tone. Dismiss button is wired to the agent panel's
 * ``clearErrors`` so closing the banner also clears the
 * underlying ``errors`` / ``rateLimit`` state on the reducer.
 */

import { X } from 'lucide-react';
import type { AgentNotice, NoticeTone } from '@/lib/agent/errors';
import { cn } from '@/lib/utils/cn';

const TONE_CLASSES: Record<NoticeTone, { wrap: string; icon: string; hint: string; button: string }> = {
  error: {
    wrap: 'bg-red-50 text-red-900 dark:bg-red-500/10 dark:text-red-100',
    icon: 'text-red-500 dark:text-red-400',
    hint: 'text-red-800/80 dark:text-red-100/80',
    button:
      'text-red-700 hover:bg-red-100 hover:text-red-900 dark:text-red-200 dark:hover:bg-red-500/20 dark:hover:text-red-100',
  },
  warning: {
    wrap: 'bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-100',
    icon: 'text-amber-500 dark:text-amber-300',
    hint: 'text-amber-800/80 dark:text-amber-100/80',
    button:
      'text-amber-800 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-200 dark:hover:bg-amber-500/20 dark:hover:text-amber-100',
  },
  neutral: {
    wrap: 'bg-gray-50 text-gray-800 dark:bg-white/[0.04] dark:text-gray-100',
    icon: 'text-gray-400 dark:text-gray-500',
    hint: 'text-gray-500 dark:text-gray-400',
    button:
      'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-gray-100',
  },
};

export interface ErrorBannerProps {
  notice?: AgentNotice | null;
  onDismiss?: () => void;
  className?: string;
}

export function ErrorBanner({ notice, onDismiss, className }: ErrorBannerProps) {
  if (!notice) return null;
  const tone = TONE_CLASSES[notice.tone];
  const Icon = notice.icon;
  return (
    <div
      role="alert"
      title={notice.raw}
      className={cn('flex flex-none select-text items-start gap-2 px-3 py-2 text-[12.5px]', tone.wrap, className)}
    >
      <Icon className={cn('mt-0.5 size-3.5 shrink-0 select-none', tone.icon)} aria-hidden />
      <div className="min-w-0 flex-1 break-words">
        <span className="font-medium">{notice.headline}</span>
        {notice.hint && <span className={cn('ml-1.5', tone.hint)}>{notice.hint}</span>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className={cn(
            '-mr-1 flex size-5 shrink-0 cursor-pointer select-none items-center justify-center rounded transition-colors duration-150',
            tone.button
          )}
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
}
